const LIMITED_SORT_VALUE = 'limited-edition-first';

const ICON_CLOSE =
  '<svg viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L17 17M17 1L1 17" stroke="currentColor" stroke-width="1.2"/></svg>';

const ICON_GRID_2 =
  '<svg viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="1" y="1" width="7" height="16" stroke="currentColor" stroke-width="1.2"/><rect x="10" y="1" width="7" height="16" stroke="currentColor" stroke-width="1.2"/></svg>';

const ICON_GRID_4 =
  '<svg viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="1" y="1" width="7" height="7" stroke="currentColor" stroke-width="1.2"/><rect x="10" y="1" width="7" height="7" stroke="currentColor" stroke-width="1.2"/><rect x="1" y="10" width="7" height="7" stroke="currentColor" stroke-width="1.2"/><rect x="10" y="10" width="7" height="7" stroke="currentColor" stroke-width="1.2"/></svg>';

/* Right-pointing chevron — matches icon-arrow.svg's native orientation, since the
   shared CSS rotates .mobile-facets__arrow the same way for every filter group. */
const ICON_CHEVRON =
  '<svg viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L5 5L1 9" stroke="currentColor" stroke-width="1.2"/></svg>';

function humanizeSlug(key) {
  return key
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/* One catalog filter category (Color, Colección, Estilo, Material). Values are
   read from a data-* attribute on each .catalog-card — single value (data-model,
   data-color) or pipe-delimited multi-value (data-materials, data-styles). */
class CatalogFilterGroup {
  constructor(grid, { key, dataAttr, multi, labelKey, defaultLabel, className, humanize = true }) {
    this.grid = grid;
    this.key = key;
    this.dataAttr = dataAttr;
    this.multi = multi;
    this.labelKey = labelKey;
    this.defaultLabel = defaultLabel;
    this.className = className;
    this.humanize = humanize;
    this.active = new Set();
  }

  getCardValues(card) {
    const raw = card.dataset[this.dataAttr];
    if (!raw) return [];
    return this.multi ? raw.split('|').filter(Boolean) : [raw];
  }

  getAllValues() {
    const values = new Set();
    this.grid.querySelectorAll('.catalog-card').forEach((card) => {
      this.getCardValues(card).forEach((value) => values.add(value));
    });
    return Array.from(values).sort();
  }

  matches(card) {
    if (this.active.size === 0) return true;
    const cardValues = this.getCardValues(card);
    return cardValues.some((value) => this.active.has(value));
  }

  render(wrapper) {
    const values = this.getAllValues();
    let details = wrapper.querySelector(`.${this.className}`);
    if (details) details.remove();
    if (values.length === 0) return;

    details = document.createElement('details');
    details.className = `mobile-facets__details ${this.className}`;
    const label = window.catalogStrings?.[this.labelKey] || this.defaultLabel;
    details.innerHTML = `
      <summary class="mobile-facets__summary focus-inset">
        <div>
          <span>${label}</span>
          <span class="mobile-facets__arrow">${ICON_CHEVRON}</span>
        </div>
      </summary>
      <div class="mobile-facets__submenu gradient">
        <ul class="facets-layout-list facets-layout-list--text mobile-facets__list list-unstyled" role="list">
          ${values
            .map(
              (value) => `
            <li class="mobile-facets__item list-menu__item">
              <label class="facets__label mobile-facets__label${this.active.has(value) ? ' active' : ''}">
                <input type="checkbox" class="mobile-facets__checkbox" data-filter-value="${value}" ${
                  this.active.has(value) ? 'checked' : ''
                }>
                <span class="facet-checkbox__text">
                  <span class="facet-checkbox__text-label">${this.humanize ? humanizeSlug(value) : value}</span>
                </span>
              </label>
            </li>`
            )
            .join('')}
        </ul>
      </div>
    `;
    wrapper.appendChild(details);

    details.querySelectorAll('[data-filter-value]').forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        const value = checkbox.dataset.filterValue;
        if (checkbox.checked) {
          this.active.add(value);
        } else {
          this.active.delete(value);
        }
        checkbox.closest('.mobile-facets__label').classList.toggle('active', checkbox.checked);
        this.grid.applyFilters();
      });
    });
  }

  clear() {
    this.active.clear();
  }
}

class CatalogGrid extends HTMLElement {
  constructor() {
    super();
    this.sortIsLimited = false;

    this.filterGroups = [
      new CatalogFilterGroup(this, {
        key: 'color',
        dataAttr: 'color',
        multi: false,
        labelKey: 'color',
        defaultLabel: 'Color',
        className: 'catalog-color-filter',
        humanize: false,
      }),
      new CatalogFilterGroup(this, {
        key: 'model',
        dataAttr: 'model',
        multi: false,
        labelKey: 'collection',
        defaultLabel: 'Colección',
        className: 'catalog-collection-filter',
      }),
      new CatalogFilterGroup(this, {
        key: 'style',
        dataAttr: 'styles',
        multi: true,
        labelKey: 'style',
        defaultLabel: 'Estilo',
        className: 'catalog-style-filter',
      }),
      new CatalogFilterGroup(this, {
        key: 'material',
        dataAttr: 'materials',
        multi: true,
        labelKey: 'material',
        defaultLabel: 'Material',
        className: 'catalog-material-filter',
      }),
    ];

    this.buildFilters();
    this.addLimitedSortOption();
    this.buildDrawerCloseButton();
    this.buildClearAllButton();
    this.buildDensityToggle();
    this.formatItemCount();

    this.gridContainer = this.querySelector('#ProductGridContainer');
    if (this.gridContainer) {
      this.observer = new MutationObserver(() => {
        this.applyFilters();
        if (this.sortIsLimited) this.applyLimitedSort();
        this.buildFilters(true);
        this.addLimitedSortOption();
      });
      this.observer.observe(this.gridContainer, { childList: true, subtree: false });
    }

    document.addEventListener(
      'change',
      (event) => {
        if (event.target.id !== 'SortBy' && event.target.id !== 'SortBy-mobile') return;
        if (event.target.value !== LIMITED_SORT_VALUE) {
          this.sortIsLimited = false;
          return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        this.sortIsLimited = true;
        this.applyLimitedSort();
      },
      { capture: true }
    );
  }

  buildFilters(refreshOnly = false) {
    const wrapper = document.getElementById('FacetsWrapperMobile');
    if (!wrapper) return;
    this.filterGroups.forEach((group) => {
      if (refreshOnly && group.active.size > 0) return;
      group.render(wrapper);
    });
  }

  applyFilters() {
    this.querySelectorAll('.catalog-card').forEach((card) => {
      const item = card.closest('.grid__item');
      if (!item) return;
      const visible = this.filterGroups.every((group) => group.matches(card));
      item.classList.toggle('catalog-grid__item--hidden', !visible);
    });
    this.formatItemCount();
  }

  clearAllFilters() {
    this.filterGroups.forEach((group) => group.clear());
    this.applyFilters();
    this.buildFilters();
  }

  addLimitedSortOption() {
    ['SortBy', 'SortBy-mobile'].forEach((id) => {
      const select = document.getElementById(id);
      if (!select || select.querySelector(`option[value="${LIMITED_SORT_VALUE}"]`)) return;
      const option = document.createElement('option');
      option.value = LIMITED_SORT_VALUE;
      option.textContent = window.catalogStrings?.limitedEdition || 'Edición limitada';
      select.appendChild(option);
    });
  }

  applyLimitedSort() {
    const list = this.querySelector('#product-grid');
    if (!list) return;
    const items = Array.from(list.children);
    items.sort((a, b) => {
      const aLimited = a.querySelector('.catalog-card')?.dataset.limited === '1' ? 1 : 0;
      const bLimited = b.querySelector('.catalog-card')?.dataset.limited === '1' ? 1 : 0;
      return bLimited - aLimited;
    });
    items.forEach((item) => list.appendChild(item));
  }

  buildDrawerCloseButton() {
    const headerInner = this.querySelector('.mobile-facets__header-inner');
    if (!headerInner || headerInner.querySelector('.catalog-header-inner__close')) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'catalog-header-inner__close';
    button.setAttribute('aria-label', window.catalogStrings?.close || 'Close');
    button.innerHTML = `<span class="svg-wrapper">${ICON_CLOSE}</span>`;
    button.addEventListener('click', () => {
      this.querySelector('.mobile-facets__wrapper summary')?.click();
    });
    headerInner.appendChild(button);
  }

  buildClearAllButton() {
    const inner = this.querySelector('.mobile-facets__inner');
    if (!inner || inner.querySelector('.catalog-filter-clear-all')) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'catalog-filter-clear-all';
    button.textContent = window.catalogStrings?.clearAll || 'Clear all';
    button.addEventListener('click', () => this.clearAllFilters());
    inner.appendChild(button);
  }

  buildDensityToggle() {
    const facetsContainer = this.querySelector('.facets-container');
    const grid = this.querySelector('#product-grid');
    if (!facetsContainer || !grid || facetsContainer.querySelector('.catalog-controls__density')) return;

    const wrap = document.createElement('div');
    wrap.className = 'catalog-controls__density';
    wrap.innerHTML = `
      <button type="button" class="catalog-controls__density-button" data-density="2" aria-label="${
        window.catalogStrings?.density2 || '2 columns'
      }">${ICON_GRID_2}</button>
      <button type="button" class="catalog-controls__density-button is-active" data-density="4" aria-label="${
        window.catalogStrings?.density4 || '4 columns'
      }">${ICON_GRID_4}</button>
    `;
    facetsContainer.appendChild(wrap);

    wrap.querySelectorAll('[data-density]').forEach((button) => {
      button.addEventListener('click', () => {
        const density = button.dataset.density;
        grid.classList.toggle('grid--4-col-desktop', density === '4');
        grid.classList.toggle('grid--2-col-desktop', density === '2');
        wrap
          .querySelectorAll('[data-density]')
          .forEach((el) => el.classList.toggle('is-active', el === button));
      });
    });
  }

  formatItemCount() {
    const template = window.catalogStrings?.itemsCount || 'Items: {{ count }}';
    const count = this.querySelectorAll('.grid__item:not(.catalog-grid__item--hidden) .catalog-card').length;
    ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      el.textContent = template.replace('{{ count }}', count);
    });
  }
}

customElements.define('catalog-grid', CatalogGrid);
